import React, { useEffect, useState, useRef } from 'react';
import { fetchCategories, fetchProducts, fetchVariantGroups, fetchActivePromotions, getCatalogId } from '../lib/api';
import { getOrgId } from '../utils/tenantBoot';
import ProductCard from './ProductCard';

/**
 * Check if running in kiosk mode
 */
const isKioskMode = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('kiosk') === '1';
};

/**
 * Get auto-scroll timing settings from backend via window global
 * These are loaded by App.jsx from the backend API
 */
const getScrollSettings = () => {
  const settings = window.__CATALOG_SETTINGS__ || {};
  
  return {
    IDLE_TIMEOUT: settings.idle_timeout || 10000, // Default 10 seconds
    AUTO_SCROLL_INTERVAL: settings.auto_scroll_interval || 2000, // Default 2 seconds
  };
};

/**
 * Shelf View Component
 * Displays products in horizontal scrollable rows grouped by category
 * Each category independently auto-scrolls after configured idle time
 * Per-category idle tracking - only the category being browsing stays paused
 * 
 * Hierarchical Navigation:
 * - Brand-first: Brand → Categories with that brand → Subcategories → Products
 * - Category-first: Category → Subcategories + Brands in category → Products
 */
export default function ShelfView({ selectedCategory, selectedBrand, selectedProductGroup, onProductSelect }) {
  const [categories, setCategories] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoScrollingCategories, setAutoScrollingCategories] = useState(new Set());
  
  // Navigation state for hierarchical drill-down
  const [navigationPath, setNavigationPath] = useState([]);
  const [currentView, setCurrentView] = useState('products'); // 'products', 'categories', 'brands'
  const [availableBrands, setAvailableBrands] = useState([]);
  const [drillDownCategory, setDrillDownCategory] = useState(null); // Category selected within brand view

  // Get scroll settings from localStorage (set by admin panel)
  const scrollSettings = useRef(getScrollSettings());
  const IDLE_TIMEOUT = scrollSettings.current.IDLE_TIMEOUT;
  const AUTO_SCROLL_INTERVAL = scrollSettings.current.AUTO_SCROLL_INTERVAL;

  // Refs for per-category auto-scroll
  const shelfRowRefs = useRef({});
  const categoryTimersRef = useRef({}); // Map of categoryId -> timeout
  const categoryScrollIntervalsRef = useRef({}); // Map of categoryId -> interval
  const categoryLastInteractionRef = useRef({}); // Map of categoryId -> timestamp

  console.log('[ShelfView] Using scroll settings:', {
    IDLE_TIMEOUT,
    AUTO_SCROLL_INTERVAL,
    idleSeconds: IDLE_TIMEOUT / 1000,
    scrollSeconds: AUTO_SCROLL_INTERVAL / 1000
  });

  useEffect(() => {
    loadShelfData();
  }, []);

  // Reload when category, brand, or product group filter changes
  useEffect(() => {
    setDrillDownCategory(null); // Reset drill-down when top-level filter changes
    loadShelfData();
  }, [selectedCategory, selectedBrand, selectedProductGroup]);

  // Per-category auto-scroll management
  useEffect(() => {
    const startCategoryAutoScroll = (categoryId) => {
      // Don't start if already scrolling
      if (categoryScrollIntervalsRef.current[categoryId]) {
        return;
      }

      const rowElement = shelfRowRefs.current[categoryId];
      if (!rowElement) {
        console.log(`[ShelfView] Cannot start auto-scroll - no element for category: ${categoryId}`);
        return;
      }

      // Check if there's actually content to scroll
      const scrollWidth = rowElement.scrollWidth;
      const clientWidth = rowElement.clientWidth;
      const canScroll = scrollWidth > clientWidth;

      console.log(`[ShelfView] Category ${categoryId} scroll info:`, {
        scrollWidth,
        clientWidth,
        canScroll,
        difference: scrollWidth - clientWidth
      });

      if (!canScroll) {
        console.log(`[ShelfView] Category ${categoryId} has no overflow - nothing to scroll`);
        // Even if no scroll needed now, set up for future
        // return; // Don't return, set it up anyway for when window resizes
      }

      console.log(`[ShelfView] ✅ Starting auto-scroll for category: ${categoryId}`);
      
      setAutoScrollingCategories(prev => new Set([...prev, categoryId]));

      categoryScrollIntervalsRef.current[categoryId] = setInterval(() => {
        const rowElement = shelfRowRefs.current[categoryId];
        if (!rowElement) return;

        const scrollWidth = rowElement.scrollWidth;
        const clientWidth = rowElement.clientWidth;
        const scrollLeft = rowElement.scrollLeft;

        // Calculate the width of one product item (first child + gap)
        const firstItem = rowElement.querySelector('.shelf-product-item');
        let scrollAmount = 180; // Default fallback
        
        if (firstItem) {
          const itemWidth = firstItem.offsetWidth;
          const computedStyle = window.getComputedStyle(rowElement);
          const gap = parseFloat(computedStyle.gap) || 3; // Updated to match tight shelf spacing
          scrollAmount = itemWidth + gap;
          
          console.log(`[ShelfView] Calculated scroll amount:`, {
            itemWidth,
            gap,
            scrollAmount
          });
        }

        // Re-check if scrollable (window might have resized)
        if (scrollWidth <= clientWidth) {
          console.log(`[ShelfView] Category ${categoryId} no longer needs scrolling`);
          return;
        }

        const isAtEnd = scrollLeft >= (scrollWidth - clientWidth - 10);

        console.log(`[ShelfView] Category ${categoryId} scrolling:`, {
          scrollLeft,
          scrollWidth,
          clientWidth,
          isAtEnd,
          maxScroll: scrollWidth - clientWidth,
          scrollingBy: scrollAmount
        });

        if (isAtEnd) {
          // Loop back to start
          console.log(`[ShelfView] Category ${categoryId} reached end, looping to start`);
          rowElement.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          // Continue scrolling by one product width
          rowElement.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }, AUTO_SCROLL_INTERVAL);
    };

    const stopCategoryAutoScroll = (categoryId) => {
      if (categoryScrollIntervalsRef.current[categoryId]) {
        clearInterval(categoryScrollIntervalsRef.current[categoryId]);
        delete categoryScrollIntervalsRef.current[categoryId];
        
        setAutoScrollingCategories(prev => {
          const newSet = new Set(prev);
          newSet.delete(categoryId);
          return newSet;
        });
        
        console.log(`[ShelfView] ⏹️ Stopped auto-scroll for category: ${categoryId}`);
      }
    };

    const resetCategoryTimer = (categoryId) => {
      // Clear existing timer
      if (categoryTimersRef.current[categoryId]) {
        clearTimeout(categoryTimersRef.current[categoryId]);
      }

      // Stop auto-scrolling for this category
      stopCategoryAutoScroll(categoryId);

      // Update last interaction time
      categoryLastInteractionRef.current[categoryId] = Date.now();

      console.log(`[ShelfView] ⏰ Timer reset for category ${categoryId} - will auto-scroll in ${IDLE_TIMEOUT / 1000} seconds`);

      // Start new timer for this category
      categoryTimersRef.current[categoryId] = setTimeout(() => {
        console.log(`[ShelfView] ⏰ Timer expired for category ${categoryId} - starting auto-scroll`);
        startCategoryAutoScroll(categoryId);
      }, IDLE_TIMEOUT);
    };

    // Initialize timers for all categories
    const initializeCategoryTimers = () => {
      const categoryIds = Object.keys(shelfRowRefs.current);
      console.log(`[ShelfView] 🚀 Initializing timers for ${categoryIds.length} categories:`, categoryIds);
      
      categoryIds.forEach(categoryId => {
        if (!categoryTimersRef.current[categoryId]) {
          resetCategoryTimer(categoryId);
        }
      });
    };

    // Set up event listeners on each category row
    const setupCategoryListeners = () => {
      console.log(`[ShelfView] 🎧 Setting up event listeners for ${Object.keys(shelfRowRefs.current).length} categories`);
      
      Object.entries(shelfRowRefs.current).forEach(([categoryId, element]) => {
        if (!element) return;

        const handleInteraction = (e) => {
          console.log(`[ShelfView] 👆 User interaction detected on category ${categoryId} (${e.type})`);
          // Only reset timer for this specific category
          resetCategoryTimer(categoryId);
        };

        // Listen for scroll, touch, and mouse events on this specific row
        element.addEventListener('scroll', handleInteraction, { passive: true });
        element.addEventListener('touchstart', handleInteraction, { passive: true });
        element.addEventListener('mousedown', handleInteraction, { passive: true });
        
        // Store event listeners for cleanup
        element._categoryEventListeners = {
          scroll: handleInteraction,
          touchstart: handleInteraction,
          mousedown: handleInteraction,
        };
      });
    };

    // Initialize when categories are loaded - use a small delay to ensure refs are attached
    if (categories.length > 0 && Object.keys(shelfRowRefs.current).length > 0) {
      console.log('[ShelfView] Categories loaded, setting up auto-scroll...');
      // Small delay to ensure DOM refs are fully attached
      const setupTimer = setTimeout(() => {
        console.log('[ShelfView] Refs available:', Object.keys(shelfRowRefs.current).length);
        setupCategoryListeners();
        initializeCategoryTimers();
      }, 100);

      return () => {
        clearTimeout(setupTimer);
        // Clear all timers
        Object.values(categoryTimersRef.current).forEach(timer => clearTimeout(timer));
        Object.values(categoryScrollIntervalsRef.current).forEach(interval => clearInterval(interval));
        
        // Remove event listeners
        Object.entries(shelfRowRefs.current).forEach(([categoryId, element]) => {
          if (element && element._categoryEventListeners) {
            const listeners = element._categoryEventListeners;
            element.removeEventListener('scroll', listeners.scroll);
            element.removeEventListener('touchstart', listeners.touchstart);
            element.removeEventListener('mousedown', listeners.mousedown);
          }
        });
      };
    }
  }, [categories, loading]);

  // Additional initialization when data finishes loading
  useEffect(() => {
    if (!loading && categories.length > 0) {
      console.log('[ShelfView] 📦 Data loaded, waiting for DOM...');
      // Give DOM time to render
      const initTimer = setTimeout(() => {
        const refCount = Object.keys(shelfRowRefs.current).length;
        console.log('[ShelfView] 🔍 Checking refs:', { 
          categories: categories.length, 
          refs: refCount,
          refKeys: Object.keys(shelfRowRefs.current)
        });
        
        if (refCount > 0) {
          console.log('[ShelfView] ✅ Refs ready, initializing auto-scroll');
          // Check if not already initialized
          if (Object.keys(categoryTimersRef.current).length === 0) {
            setupCategoryListeners();
            initializeCategoryTimers();
          }
        } else {
          console.warn('[ShelfView] ⚠️ No refs found after delay');
        }
      }, 500); // Longer delay to ensure rendering

      return () => clearTimeout(initTimer);
    }
  }, [loading]);

  const loadShelfData = async () => {
    try {
      setLoading(true);
      setError(null);
      const mode = isKioskMode() ? 'catalog' : 'ecommerce';
      const baseUrl = import.meta.env.VITE_RCC_BASE || '';
      const orgId = getOrgId();
      const catalogId = await getCatalogId();
      const catalogParam = catalogId ? `&catalog_id=${catalogId}` : '';

      // Fetch active promotions AND variant groups ONCE up-front so every branch below
      // (category, brand, product-group, and default views) can attach the SAME
      // promotion/variant data to products. Root-cause fix: previously variant groups
      // were only fetched inside the "default / All Products" branch, so selecting a
      // category or brand silently dropped variant-group data for Shelf mode (unlike
      // CatalogView's main grid, which always had it) - Product Detail then opened
      // with no variantData and showed no variants/attributes/images.
      const [promotionsData, variantGroupsData] = await Promise.all([
        fetchActivePromotions().catch(err => {
          console.warn('[ShelfView] Failed to load promotions (non-critical):', err);
          return { promotions: [], groups: [], group_items: [] };
        }),
        fetchVariantGroups(getOrgId())
      ]);
      const barcodePromotionMap = {};
      if (promotionsData && promotionsData.promotions && promotionsData.group_items) {
        const { promotions, groups: promoGroups, group_items } = promotionsData;

        const promoMap = {};
        promotions.forEach(promo => {
          promoMap[promo.promo_id] = promo;
        });

        const groupMap = {};
        (promoGroups || []).forEach(group => {
          const key = `${group.promo_id}_${group.group_id}`;
          groupMap[key] = group;
        });

        group_items.forEach(item => {
          if (item.barcode) {
            const promo = promoMap[item.promo_id];
            const rewardGroupKey = Object.keys(groupMap).find(key =>
              key.startsWith(`${item.promo_id}_`) && groupMap[key].kind === 'REWARD'
            );
            const qualifierGroupKey = Object.keys(groupMap).find(key =>
              key.startsWith(`${item.promo_id}_`) && groupMap[key].kind === 'QUALIFIER'
            );

            if (promo && rewardGroupKey) {
              const rewardGroup = groupMap[rewardGroupKey];
              const qualifierGroup = qualifierGroupKey ? groupMap[qualifierGroupKey] : null;

              let promoText = promo.name;

              if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'PRICE_OVERRIDE') {
                promoText = `${qualifierGroup.min_qty} for £${rewardGroup.reward_value}`;
              } else if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'MONEY_OFF') {
                promoText = `${qualifierGroup.min_qty} for £${rewardGroup.reward_value}`;
              } else if (qualifierGroup && qualifierGroup.min_qty && rewardGroup.reward_type === 'PERCENT_OFF') {
                promoText = `Buy ${qualifierGroup.min_qty} Get ${rewardGroup.reward_value}% Off`;
              } else if (rewardGroup.reward_type === 'MONEY_OFF') {
                promoText = `£${rewardGroup.reward_value} Off`;
              } else if (rewardGroup.reward_type === 'PERCENT_OFF') {
                promoText = `${rewardGroup.reward_value}% Off`;
              }

              // Store full promotion metadata for basket discount calculations
              barcodePromotionMap[item.barcode] = {
                text: promoText,
                promo_id: promo.promo_id,
                promo_name: promo.name,
                group_id: item.group_id, // Include group_id for meal deal validation
                min_qty: qualifierGroup?.min_qty || 1,
                reward_type: rewardGroup.reward_type,
                reward_value: rewardGroup.reward_value
              };
              console.log(`[ShelfView] ✅ Mapped barcode ${item.barcode} to promotion:`, barcodePromotionMap[item.barcode]);
            }
          }
        });
      }
      console.log('[ShelfView] Barcode promotion map:', barcodePromotionMap);

      // Map of product_id -> variant group data, built once and reused by every
      // branch below (same shape/source CatalogView's default grid already uses).
      const productVariantMap = {};
      (variantGroupsData?.groups || []).forEach(group => {
        if (group.products && group.products.length > 0) {
          group.products.forEach(prod => {
            const productId = prod.product_id;
            if (!productVariantMap[productId]) {
              productVariantMap[productId] = {
                groupName: group.group_name,
                groupId: group.group_id,
                attributes: prod.attributes || [],
                variants: group.products.filter(p => p.product_id !== productId)
              };
            }
          });
        }
      });

      // Reused by every branch below to attach promotion AND variant-group data to a
      // list of products, instead of re-deriving/duplicating that logic per branch.
      const enrichWithPromotion = (products) => (products || []).map(p => ({
        ...p,
        promotion: p.barcode ? (barcodePromotionMap[p.barcode] || null) : null,
        variantData: productVariantMap[p.product_id] || null
      }));

      // Handle category selection — show subcategories AND brands in that category
      if (selectedCategory) {
        console.log('[ShelfView] Loading products for category:', selectedCategory.name);
        const allCats = await fetchCategories(mode);
        const subCats = (allCats || []).filter(c => c.parent_id === selectedCategory.category_id);
        
        // Get all products in this category and subcategories to find available brands
        const categoryProducts = await fetchProducts(selectedCategory.category_id, mode);
        const allProducts = enrichWithPromotion(categoryProducts || []);
        
        // Extract unique brands from products
        const brandMap = {};
        allProducts.forEach(p => {
          if (p.brand_id && p.brand_name) {
            brandMap[p.brand_id] = {
              brand_id: p.brand_id,
              name: p.brand_name,
              products: []
            };
          }
        });
        
        // Populate brand products
        allProducts.forEach(p => {
          if (p.brand_id && brandMap[p.brand_id]) {
            brandMap[p.brand_id].products.push(p);
          }
        });
        
        const brandsInCategory = Object.values(brandMap);
        console.log('[ShelfView] Found brands in category:', brandsInCategory.length);
        
        if (subCats.length > 0) {
          // Has subcategories — show each as a shelf row, then brands
          const prods = {};
          for (const sub of subCats) {
            const subProducts = enrichWithPromotion(await fetchProducts(sub.category_id, mode));
            if (subProducts && subProducts.length > 0) {
              prods[sub.category_id] = subProducts;
            }
          }
          // Also show products directly in the parent category
          if (allProducts.length > 0) {
            prods[selectedCategory.category_id] = allProducts;
            setCategories([selectedCategory, ...subCats.filter(s => prods[s.category_id])]);
          } else {
            setCategories(subCats.filter(s => prods[s.category_id]));
          }
          
          // Add brand rows
          brandsInCategory.forEach(brand => {
            const brandId = `brand_${brand.brand_id}`;
            prods[brandId] = brand.products;
            // Add category object for brand row
            const brandCategory = {
              category_id: brandId,
              name: `Brand: ${brand.name}`,
              product_count: brand.products.length,
              emoji: '🏷️'
            };
            setCategories(prev => [...prev, brandCategory]);
          });
          
          setCategoryProducts(prods);
        } else {
          // No subcategories — show products and brands
          const cats = [selectedCategory];
          const prods = { [selectedCategory.category_id]: allProducts };
          
          // Add brand rows
          brandsInCategory.forEach(brand => {
            const brandId = `brand_${brand.brand_id}`;
            prods[brandId] = brand.products;
            cats.push({
              category_id: brandId,
              name: `Brand: ${brand.name}`,
              product_count: brand.products.length,
              emoji: '🏷️'
            });
          });
          
          setCategories(cats);
          setCategoryProducts(prods);
        }
        setLoading(false);
        return;
      }

      // Handle brand filtering — show categories, then drill into products
      if (selectedBrand) {
        console.log('[ShelfView] Loading products for brand:', selectedBrand.name);
        const response = await fetch(`${baseUrl}/rcc-api/brands/${selectedBrand.brand_id}/products?mode=${mode}&org_id=${orgId}${catalogParam}`);
        if (!response.ok) throw new Error('Failed to load brand products');
        
        const data = await response.json();
        const products = enrichWithPromotion(data.products || []);
        
        if (drillDownCategory) {
          // User clicked a category - show only products from that brand+category
          console.log('[ShelfView] Drill-down view: brand + category');
          const allCats = await fetchCategories(mode);
          const targetCat = allCats.find(c => c.category_id === drillDownCategory.category_id);
          const subCats = (allCats || []).filter(c => c.parent_id === drillDownCategory.category_id);
          
          const filteredProducts = products.filter(p => p.category_id === drillDownCategory.category_id);
          
          if (subCats.length > 0) {
            // Show subcategories as separate rows
            const prods = {};
            for (const sub of subCats) {
              const subProducts = products.filter(p => p.category_id === sub.category_id);
              if (subProducts.length > 0) {
                prods[sub.category_id] = subProducts;
              }
            }
            // Also show products directly in parent category
            if (filteredProducts.length > 0) {
              prods[drillDownCategory.category_id] = filteredProducts;
              setCategories([drillDownCategory, ...subCats.filter(s => prods[s.category_id])]);
            } else {
              setCategories(subCats.filter(s => prods[s.category_id]));
            }
            setCategoryProducts(prods);
          } else {
            // No subcategories - show products directly
            setCategories([drillDownCategory]);
            setCategoryProducts({ [drillDownCategory.category_id]: filteredProducts });
          }
        } else {
          // Show brand products grouped by category (hierarchical view)
          console.log('[ShelfView] Brand top-level view: showing categories');
          const grouped = {};
          for (const p of products) {
            const catId = p.category_id || 'uncategorized';
            const catName = p.category_name || 'Other';
            if (!grouped[catId]) grouped[catId] = { category_id: catId, name: catName, products: [] };
            grouped[catId].products.push(p);
          }
          const cats = Object.values(grouped);
          if (cats.length > 0) {
            setCategories(cats.map(c => ({ category_id: c.category_id, name: c.name, product_count: c.products.length })));
            const prods = {};
            cats.forEach(c => { prods[c.category_id] = c.products; });
            setCategoryProducts(prods);
          } else {
            setCategories([{ category_id: 'brand-filter', name: `Brand: ${selectedBrand.name}`, product_count: 0 }]);
            setCategoryProducts({ 'brand-filter': [] });
          }
        }
        setLoading(false);
        return;
      }

      // Handle product group filtering
      if (selectedProductGroup) {
        console.log('[ShelfView] Loading products for group:', selectedProductGroup.group_name);
        const response = await fetch(`${baseUrl}/rcc-api/product-groups/${selectedProductGroup.group_id}/products?mode=${mode}&org_id=${orgId}${catalogParam}`);
        if (!response.ok) throw new Error('Failed to load group products');
        
        const data = await response.json();
        const products = enrichWithPromotion(data.products || []);
        
        // Create a single "category" for the product group
        setCategories([{ category_id: 'group-filter', name: selectedProductGroup.group_name, product_count: products.length }]);
        setCategoryProducts({ 'group-filter': products });
        setLoading(false);
        return;
      }
      
      // Default: Load all categories and their products
      // Variant groups + promotions were already fetched once above and are reused
      // here via enrichWithPromotion (which also attaches variantData) to avoid
      // duplicating that logic per branch.
      const categoriesData = await fetchCategories(mode);
      
      const cats = categoriesData || [];
      setCategories(cats);
      
      // Fetch products for each category
      const productsMap = {};
      await Promise.all(
        cats.map(async (cat) => {
          try {
            const productsData = await fetchProducts(cat.category_id, mode);
            const productsList = productsData || [];
            
            // enrichWithPromotion already attaches variantData (built once above)
            const enrichedProducts = enrichWithPromotion(productsList);
            
            productsMap[cat.category_id] = enrichedProducts;
          } catch (err) {
            console.error(`Failed to load products for category ${cat.category_id}:`, err);
            productsMap[cat.category_id] = [];
          }
        })
      );
      
      setCategoryProducts(productsMap);
    } catch (err) {
      console.error('Failed to load shelf data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: 'var(--spacing-lg)', 
        textAlign: 'center',
        color: 'var(--color-text-secondary)' 
      }}>
        Loading shelf...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: 'var(--spacing-lg)', 
        textAlign: 'center',
        color: 'var(--color-error)' 
      }}>
        Error loading shelf: {error}
      </div>
    );
  }

  return (
    <div className="shelf-view">
      {/* Breadcrumb Navigation */}
      {(drillDownCategory || selectedBrand || selectedCategory) && (
        <div style={{
          padding: '12px 16px',
          background: '#f8fafc',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
        }}>
          <span style={{ color: '#64748b' }}>🏠</span>
          {selectedBrand && (
            <>
              <span style={{ color: '#64748b' }}>→</span>
              <span style={{ fontWeight: 600, color: '#0ea5e9' }}>{selectedBrand.name}</span>
            </>
          )}
          {selectedCategory && !selectedBrand && (
            <>
              <span style={{ color: '#64748b' }}>→</span>
              <span style={{ fontWeight: 600, color: '#0ea5e9' }}>{selectedCategory.name}</span>
            </>
          )}
          {drillDownCategory && (
            <>
              <span style={{ color: '#64748b' }}>→</span>
              <span style={{ fontWeight: 600, color: '#0ea5e9' }}>{drillDownCategory.name}</span>
              <button 
                onClick={() => setDrillDownCategory(null)}
                style={{
                  marginLeft: 'auto',
                  padding: '4px 12px',
                  background: '#fff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                ← Back
              </button>
            </>
          )}
        </div>
      )}
      
      {autoScrollingCategories.size > 0 && (
        <div style={{
          position: 'fixed',
          top: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '14px',
          zIndex: 1000,
          pointerEvents: 'none',
        }}>
          🔄 {autoScrollingCategories.size} {autoScrollingCategories.size === 1 ? 'category' : 'categories'} auto-scrolling
        </div>
      )}
      
      {categories.map((category) => {
        const products = categoryProducts[category.category_id] || [];
        if (products.length === 0) return null;

        const isThisCategoryScrolling = autoScrollingCategories.has(category.category_id);
        const canDrillDown = selectedBrand && !drillDownCategory; // Can click category when in brand view

        return (
          <div key={category.category_id} className="shelf-row">
            {/* Category Label (like a shelf aisle sign) */}
            <div 
              className="shelf-category-label" 
              style={{
                opacity: isThisCategoryScrolling ? 0.7 : 1,
                transition: 'opacity 0.3s ease',
                cursor: canDrillDown ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (canDrillDown) {
                  setDrillDownCategory(category);
                }
              }}
            >
              {category.emoji && <span className="shelf-category-emoji">{category.emoji}</span>}
              <span className="shelf-category-name">{category.name}</span>
              <span className="shelf-category-count">{products.length} items</span>
              {canDrillDown && (
                <span style={{ marginLeft: '8px', fontSize: '14px', color: '#0ea5e9' }}>→</span>
              )}
              {isThisCategoryScrolling && (
                <span style={{ marginLeft: 'auto', fontSize: '16px' }}>🔄</span>
              )}
            </div>
            
            {/* Horizontal scrollable product row */}
            <div 
              className="shelf-products-row"
              ref={(el) => {
                if (el) {
                  shelfRowRefs.current[category.category_id] = el;
                }
              }}
            >
              {products.map((product) => (
                <div 
                  key={product.product_id} 
                  className="shelf-product-item"
                  onClick={() => onProductSelect(product)}
                >
                  <div className="product-card" data-product="true">
                    <ProductCard 
                      product={product} 
                      hideButton={true}
                      showBarcode={true}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
